import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Typography, 
  Box, 
  Container, 
  Paper, 
  Divider, 
  TextField, 
  Button,
  Avatar,
  Skeleton,
  Alert,
  CircularProgress
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import useAxiosAuth from '../../hooks/useAxiosAuth';
import { getInternalBlogId, PERSONA_DISPLAY_NAMES } from '../../utils/personaUtils';


const BlogPostPage = ({ user, onInteraction }) => {
  const { blogId: urlBlogId } = useParams();
  const internalBlogId = getInternalBlogId(urlBlogId);
  const navigate = useNavigate();
  
  const [blog, setBlog] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Comment form state
  const [name, setName] = useState('');
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  
  // Use your existing axios auth hook
  const axios = useAxiosAuth();
  // const defaultHeaders = {
  //   'Content-Type': 'application/json'
  // };

  useEffect(() => {
    const fetchBlogData = async () => {
      // Only fetch if user is authenticated
      if (!user) {
        return;
      }
      
      try {
        setLoading(true);
        
        // Fetch blog content and questions
        const response = await axios.get(`/blog/${internalBlogId}`);
        
        setBlog(response.data.blog);
        setQuestions(response.data.questions || []);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching blog data:', err);
        setError('Failed to load blog content. Please try again later.');
        setLoading(false);
      }
    };
    
    fetchBlogData();
  }, [internalBlogId, user, axios]);

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    
    // Check if user is logged in
    if (!user) {
      onInteraction();
      return;
    }
    
    if (!name.trim() || !comment.trim()) {
      setSubmitError('Please enter both your name and comment.');
      return;
    }
    
    try {
      setSubmitting(true);
      setSubmitError(null);
      
      // Post comment to API
      await axios.post(
        `/blog/${internalBlogId}/question`, 
        { question: comment, name }
      );
      
      // Clear form
      setName('');
      setComment('');
      setSubmitSuccess(true);
      
      // Show success message briefly
      setTimeout(() => {
        setSubmitSuccess(false);
      }, 5000);
      
      // Fetch updated questions
      const response = await axios.get(`/blog/${internalBlogId}`);
      
      setQuestions(response.data.questions || []);
      
    } catch (err) {
      console.error('Error submitting comment:', err);
      setSubmitError(err.response?.data?.detail || 'Failed to submit your comment. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };
  
  // Format blog content with proper paragraph breaks
  const formatContent = (content) => {
    if (!content) return '';
    
    return content.split('\n\n')
      .filter(para => para.trim() !== '')
      .map((para, index) => (
        <Typography key={index} paragraph>
          {para}
        </Typography>
      ));
  };
  
  // Format timestamp to readable date
  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    
    const date = new Date(parseInt(timestamp) * 1000);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Container maxWidth="md">
        <Box sx={{ my: 4 }}>
          <Skeleton variant="text" height={60} width="80%" sx={{ mb: 1 }} />
          <Skeleton variant="text" height={30} width="40%" sx={{ mb: 4 }} />
          
          <Skeleton variant="text" height={20} width="100%" />
          <Skeleton variant="text" height={20} width="100%" />
          <Skeleton variant="text" height={20} width="95%" />
          <Skeleton variant="text" height={20} width="98%" />
          <Skeleton variant="text" height={20} width="60%" sx={{ mb: 2 }} />
          
          <Skeleton variant="text" height={20} width="100%" />
          <Skeleton variant="text" height={20} width="100%" />
          <Skeleton variant="text" height={20} width="90%" />
          <Skeleton variant="text" height={20} width="95%" />
          <Skeleton variant="text" height={20} width="70%" />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm">
        <Paper
          elevation={3}
          sx={{
            p: 4,
            mt: 6,
            textAlign: 'center',
            borderRadius: 2
          }}
        >
          <Typography variant="h5" color="error" gutterBottom>
            {error}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate('/personal-projects')}
            sx={{ mt: 2 }}
          >
            Back to Experts
          </Button>
        </Paper>
      </Container>
    );
  }

const getBlogImage = () => {
  if (!blog) return null;
  
  // Map of blog IDs to image paths
  const blogImageMap = {
    'restoration_artisan_vinatage_dresser_vanity': 'generated_image_dresser_vantity4.png',
    'house_doctor_garage_wall_tool_organization': 'generated_image_modular_garage.png',
    'everyday_woodsmith_slding_barn_door': 'generated_image_sliding_bsrn_kitchen_door.png',
    // Add more mappings as needed
  };

  // Try to get the specific image for this blog using the URL parameter
  return blogImageMap[urlBlogId] || null;
};

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <Container maxWidth="md">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
{/* Blog Header */}
<Box sx={{ my: 4 }}>   
  {/* Breadcrumb navigation */}
  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
    <Typography variant="body2" sx={{ color: '#666' }}>
      <Button
        variant="text"
        size="small"
        onClick={() => navigate('/')}
        sx={{ 
          fontWeight: 500,
          minWidth: 'auto',
          px: 1
        }}
      >
        Home
      </Button>
      {' > '}
      <Button
        variant="text"
        size="small"
        onClick={() => navigate('/blogs')}
        sx={{ 
          fontWeight: 500,
          minWidth: 'auto',
          px: 1
        }}
      >
        Articles
      </Button>
      {' > '}
      <Typography component="span" sx={{ color: '#1e3c72', fontWeight: 500 }}>
        Current Article
      </Typography>
    </Typography>
  </Box>
    {blog?.id && (
  <Box 
    sx={{ 
      width: '100%',
      mb: 4,
      borderRadius: '8px',
      overflow: 'hidden',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
    }}
  >
    <img
      src={`/${getBlogImage(blog.id)}`}
      alt={blog.title || 'Blog image'}
      style={{
        width: '100%',
        height: 'auto',
        display: 'block'
      }}
    />
  </Box>
)}
            <motion.div
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <Typography 
                variant="h1" 
                gutterBottom
                sx={{
                  background: 'linear-gradient(90deg, #1e3c72 0%, #2a5298 100%)',
                  backgroundClip: 'text',
                  textFillColor: 'transparent',
                  fontWeight: 700
                }}
              >
                {blog?.title}
              </Typography>
            </motion.div>
            
  <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
  <Avatar 
    sx={{ 
      bgcolor: 'primary.main', 
      mr: 2 
    }}
  >
    {blog?.persona_id?.charAt(0)?.toUpperCase() || 'A'}
  </Avatar>
  <Box>
    <Typography variant="subtitle1" fontWeight="bold">
      {PERSONA_DISPLAY_NAMES[blog?.persona_id] || blog?.persona_id || 'Author'}
    </Typography>
  </Box>
</Box>
            
            <Divider sx={{ mb: 4 }} />
          </Box>
          
          {/* Blog Content */}
          <Paper
            elevation={2}
            sx={{
              p: 4,
              borderRadius: 2,
              mb: 6
            }}
          >
            {formatContent(blog?.content)}
          </Paper>
          
          {/* Comments Section */}
          <Box sx={{ mb: 6 }}>
            <Typography variant="h4" component="h2" gutterBottom>
              Join the Discussion
            </Typography>
            
            {/* Comment Form */}
            <Paper
              elevation={2}
              sx={{
                p: 3,
                borderRadius: 2,
                mb: 4,
                bgcolor: 'background.paper'
              }}
            >
              <form onSubmit={handleSubmitComment}>
                <Typography variant="h6" gutterBottom>
                  Share Your Thoughts
                </Typography>
                
                <TextField
                  label="Your Name"
                  fullWidth
                  variant="outlined"
                  margin="normal"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={submitting}
                  required
                />
                
                <TextField
                  label="Your Comment"
                  fullWidth
                  variant="outlined"
                  margin="normal"
                  multiline
                  rows={4}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  disabled={submitting}
                  required

                  sx={{ mb: 2 }}
                />
                
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={submitting}
                  startIcon={submitting ? <CircularProgress size={20} /> : null}
                  sx={{
                    boxShadow: '0 4px 14px rgba(0,0,0,0.15)',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 20px rgba(0,0,0,0.2)'
                    },
                    transition: 'all 0.3s'
                  }}
                >
                  {submitting ? 'Posting...' : 'Post Comment'}
                </Button>
                
                <AnimatePresence>
                  {submitSuccess && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                    >
                      <Alert severity="success" sx={{ mt: 2 }}>
                        Your comment has been posted! The author has responded.
                      </Alert>
                    </motion.div>
                  )}
                  
                  {submitError && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                    >
                      <Alert severity="error" sx={{ mt: 2 }}>
                        {submitError}
                      </Alert>
                    </motion.div>
                  )}
                </AnimatePresence>
              </form>
            </Paper>
            
            {/* Comments Display */}
            {questions.length === 0 ? (
              <Paper 
                sx={{ 
                  p: 3, 
                  textAlign: 'center', 
                  borderRadius: 2,
                  bgcolor: 'background.paper'
                }}
              >
                <Typography variant="subtitle1" color="text.secondary">
                  Be the first to comment on this post!
                </Typography>
              </Paper>
            ) : (
              <Box>
                {questions.map((qa, index) => (
                  <Box key={qa.timestamp} sx={{ mb: 4 }}>
                    {/* User Comment */}
                    <Paper
                      elevation={1}
                      sx={{
                        p: 3,
                        borderRadius: 2,
                        mb: 2,
                        borderLeft: '4px solid',
                        borderColor: 'primary.light',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Avatar sx={{ bgcolor: 'grey.300', mr: 2 }}>
                          U
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2">
                            User
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(qa.timestamp)}
                          </Typography>
                        </Box>
                      </Box>
                      <Typography variant="body1">
                        {qa.question}
                      </Typography>
                    </Paper>
                    
                    {/* AI Response */}
                    <Paper
                      elevation={1}
                      sx={{
                        p: 3,
                        borderRadius: 2,
                        ml: 4,
                        borderLeft: '4px solid',
                        borderColor: 'primary.main',
                        bgcolor: 'rgba(33, 150, 243, 0.04)'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                          {blog?.persona_id?.charAt(0)?.toUpperCase() || 'A'}
                        </Avatar>
                        <Box>
                      <Typography variant="subtitle2">
                       {PERSONA_DISPLAY_NAMES[blog?.persona_id] || blog?.persona_id || 'Author'} 
                       <Box 
                        component="span" 
                       sx={{ 
                          ml: 1,
                          bgcolor: 'primary.main',
                          color: 'white',
                          py: 0.5,
                          px: 1,
                          borderRadius: 1,
                         fontSize: '0.7rem'
                         }}
                         >
                        Author
                       </Box>
                       </Typography>
                      <Typography variant="caption" color="text.secondary">
                       {formatDate(qa.timestamp)}
                       </Typography>
                        </Box>
                      </Box>
                      {formatContent(qa.answer)}
                    </Paper>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default BlogPostPage;  